import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { initParticleField } from "./engine";
import {
  EASTER_EGGS,
  KONAMI_BUFFER_LEN,
  getDateTheme,
  themeBackground,
  type ThemeName,
} from "./themes";

export interface SignInParticlesProps {
  className?: string;
  /** Pin a theme instead of letting the date pick one. When set, the keyboard
   *  easter eggs are off — the caller owns the theme. Used by the Design
   *  System preview. */
  theme?: ThemeName;
  /** Fill the nearest positioned ancestor instead of the viewport. */
  contained?: boolean;
}

/** Animated backdrop for the sign-in screen — a mouse-reactive dot plexus that
 *  swaps itself out for a seasonal theme on the right dates, plus a set of
 *  keyboard easter eggs. Ported from the CinchAI auth page so both products
 *  behave the same; see themes.ts for the date windows and the egg codes.
 *
 *  Click the backdrop for the per-theme payoff (big bang, confetti cannon,
 *  fireworks, Death Star). Static under prefers-reduced-motion. */
export function SignInParticles({ className, theme, contained = false }: SignInParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [dateTheme] = useState<ThemeName>(() => getDateTheme());
  const [eggTheme, setEggTheme] = useState<ThemeName | null>(null);
  const locked = theme !== undefined;
  const activeTheme = theme ?? eggTheme ?? dateTheme;

  // Type an egg code (see EASTER_EGGS) to restart the field in that theme.
  useEffect(() => {
    if (locked) return;
    let buffer = "";
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      buffer = (buffer + e.key).slice(-KONAMI_BUFFER_LEN);
      for (const [code, name] of Object.entries(EASTER_EGGS)) {
        if (buffer.endsWith(code)) {
          setEggTheme(name);
          buffer = "";
          break;
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [locked]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cleanup: (() => void) | undefined;
    // Defer one tick so the sign-in card paints and is interactive first.
    const startId = window.setTimeout(() => {
      cleanup = initParticleField(canvas, {
        theme: activeTheme,
        bounds: contained ? "element" : "viewport",
      });
    }, 0);
    return () => {
      window.clearTimeout(startId);
      cleanup?.();
    };
  }, [activeTheme, contained]);

  // The OG pattern and the Spark gradient take over the page background. The
  // engine never touches page DOM, so this is a plain layer under the canvas.
  const bg = useMemo(() => themeBackground(activeTheme), [activeTheme]);
  const layer = contained ? "absolute inset-0" : "fixed inset-0";

  return (
    <>
      {bg ? <div aria-hidden className={cn(layer, "z-0")} style={bg} /> : null}
      {/* h-full w-full is load-bearing: a canvas is a replaced element, so
          inset-0 alone leaves it at its intrinsic 300x150 instead of filling
          the box — which the engine would then measure and animate at. */}
      <canvas
        ref={canvasRef}
        aria-hidden
        className={cn(layer, "z-0 h-full w-full pointer-events-none", className)}
      />
    </>
  );
}
